import { useLingui } from '@lingui/react/macro'
import * as Popover from '@radix-ui/react-popover'
import {
  type FeedbackTarget,
  useChatbotFeedbackOptional,
} from 'features/chatbot/contexts/ChatbotFeedbackContext'
import { Check, MessageSquare, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const POPOVER_SHADOW = 'rgba(0, 0, 0, 0.3)'
const PRIMARY_BUTTON_TEXT = '#fff'

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.125rem;
`

const IconButton = styled.button<{ $visible?: boolean; $isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  opacity: ${({ $visible }) => ($visible === false ? 0 : 1)};
  transition:
    opacity 0.15s ease,
    background 0.15s ease;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.forecast.primary : theme.forecast.tertiaryText};

  &:hover {
    background: ${({ theme }) => theme.forecast.background1};
    color: ${({ theme, $isActive }) =>
      $isActive ? theme.forecast.primary : theme.forecast.secondaryText};
  }

  svg {
    width: 0.75rem;
    height: 0.75rem;
    fill: ${({ $isActive, theme }) =>
      $isActive ? theme.forecast.primary : 'transparent'};
  }
`

const CommentPopoverContent = styled(Popover.Content)`
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.375rem;
  padding: 0.5rem;
  box-shadow: 0 4px 12px ${POPOVER_SHADOW};
  z-index: 1000;
  width: 16rem;
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 3rem;
  padding: 0.375rem;
  background: ${({ theme }) => theme.forecast.background1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-family: inherit;
  font-size: 0.6875rem;
  resize: none;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.forecast.tertiaryText};
  }
`

const CommentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  margin-top: 0.375rem;
`

const SmallButton = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  background: ${({ theme, $primary }) =>
    $primary ? theme.forecast.primary : 'transparent'};
  border: 1px solid
    ${({ theme, $primary }) =>
      $primary ? theme.forecast.primary : theme.forecast.border};
  border-radius: 0.25rem;
  color: ${({ theme, $primary }) =>
    $primary ? PRIMARY_BUTTON_TEXT : theme.forecast.secondaryText};
  font-size: 0.625rem;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${({ theme, $primary }) =>
      $primary ? theme.forecast.hoverButton : theme.forecast.background1};
  }

  svg {
    width: 0.625rem;
    height: 0.625rem;
  }
`

interface FeedbackMenuProps {
  target: FeedbackTarget
  alwaysVisible?: boolean
}

export const FeedbackMenu: React.FC<FeedbackMenuProps> = ({
  target,
  alwaysVisible = false,
}) => {
  const { t } = useLingui()
  const feedbackContext = useChatbotFeedbackOptional()
  const [isHovered, setIsHovered] = useState(false)
  const [isCommentOpen, setIsCommentOpen] = useState(false)
  const [localComment, setLocalComment] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const currentFeedback = feedbackContext?.getFeedback(target.key)
  const hasRating =
    currentFeedback?.rating !== undefined && currentFeedback?.rating !== null
  const hasComment =
    currentFeedback?.comment !== undefined && currentFeedback?.comment !== ''

  const handleRatingClick = useCallback(
    (rating: 'up' | 'down') => {
      if (!feedbackContext) return
      const current = feedbackContext.getFeedback(target.key)
      if (current?.rating === rating) {
        feedbackContext.setRating(target, null)
      } else {
        feedbackContext.setRating(target, rating)
      }
    },
    [feedbackContext, target]
  )

  const handleSaveComment = useCallback(() => {
    if (!feedbackContext) return
    feedbackContext.setComment(target, localComment.trim())
    setIsCommentOpen(false)
  }, [feedbackContext, target, localComment])

  const handleCancelComment = useCallback(() => {
    setLocalComment('')
    setIsCommentOpen(false)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSaveComment()
      } else if (e.key === 'Escape') {
        handleCancelComment()
      }
    },
    [handleSaveComment, handleCancelComment]
  )

  // Initialize local comment when opening
  useEffect(() => {
    if (isCommentOpen) {
      setLocalComment(currentFeedback?.comment ?? '')
      setTimeout(() => textareaRef.current?.focus(), 0)
    }
  }, [isCommentOpen, currentFeedback?.comment])

  if (!feedbackContext) {
    return null
  }

  const shouldShow =
    alwaysVisible || isHovered || hasRating || hasComment || isCommentOpen

  return (
    <MenuContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <IconButton
        $visible={shouldShow}
        $isActive={currentFeedback?.rating === 'up'}
        onClick={() => handleRatingClick('up')}
        aria-label={t`Good response`}>
        <ThumbsUp />
      </IconButton>
      <IconButton
        $visible={shouldShow}
        $isActive={currentFeedback?.rating === 'down'}
        onClick={() => handleRatingClick('down')}
        aria-label={t`Bad response`}>
        <ThumbsDown />
      </IconButton>
      <Popover.Root open={isCommentOpen} onOpenChange={setIsCommentOpen}>
        <Popover.Trigger asChild>
          <IconButton
            $visible={shouldShow}
            $isActive={hasComment}
            aria-label={hasComment ? t`Edit comment` : t`Add comment`}>
            <MessageSquare />
          </IconButton>
        </Popover.Trigger>
        <Popover.Portal>
          <CommentPopoverContent align='start' sideOffset={4}>
            <CommentTextarea
              ref={textareaRef}
              placeholder={t`What could be improved?`}
              value={localComment}
              onChange={(e) => setLocalComment(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <CommentActions>
              <SmallButton onClick={handleCancelComment}>
                <X />
              </SmallButton>
              <SmallButton $primary onClick={handleSaveComment}>
                <Check />
              </SmallButton>
            </CommentActions>
          </CommentPopoverContent>
        </Popover.Portal>
      </Popover.Root>
    </MenuContainer>
  )
}

/**
 * A wrapper component that shows FeedbackMenu on hover of its children
 */
interface FeedbackMenuWrapperProps {
  target: FeedbackTarget
  children: React.ReactNode
}

const WrapperContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  &:hover ${IconButton} {
    opacity: 1;
  }
`

const MenuWrapper = styled.div`
  padding-top: 0.25rem;
`

export const FeedbackMenuWrapper: React.FC<FeedbackMenuWrapperProps> = ({
  target,
  children,
}) => {
  const feedbackContext = useChatbotFeedbackOptional()

  if (!feedbackContext) {
    return <>{children}</>
  }

  return (
    <WrapperContainer>
      {children}
      <MenuWrapper>
        <FeedbackMenu target={target} />
      </MenuWrapper>
    </WrapperContainer>
  )
}
